/**
 * Pure helpers: turn a saved statement (header + statement_line rows, as
 * returned by GET /api/report/statements/:id) back into the props the
 * StatementPdf component expects. Data in, data out.
 */

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS_LONG = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

/** Parse a date-only ISO string ("2026-06-01...") into UTC parts, or null. */
const utcParts = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return { y: date.getUTCFullYear(), m: date.getUTCMonth(), d: date.getUTCDate() };
};

/**
 * Build a human-readable period label from the saved period fields.
 * - MONTH:  "June 2026"
 * - CUSTOM: "Jun 1 – Jun 30, 2026"
 */
export const resolveMonthLabel = ({ period_mode, period_start, period_end }) => {
  const start = utcParts(period_start);
  if (!start) return "";

  if (period_mode === "MONTH") {
    return `${MONTHS_LONG[start.m]} ${start.y}`;
  }

  const end = utcParts(period_end);
  if (!end) {
    return `${MONTHS_SHORT[start.m]} ${start.d}, ${start.y}`;
  }
  const startLabel = start.y === end.y
    ? `${MONTHS_SHORT[start.m]} ${start.d}`
    : `${MONTHS_SHORT[start.m]} ${start.d}, ${start.y}`;
  return `${startLabel} \u2013 ${MONTHS_SHORT[end.m]} ${end.d}, ${end.y}`;
};

/** Map a statement_line row to the flat invoice shape StatementPdf renders. */
const toInvoice = (line) => ({
  id: line.id,
  invoiceNumber: line.invoice_number,
  dealerName: line.dealer_name,
  invoiceDate: line.invoice_date,
  invoiceTotal: line.invoice_total || 0,
  rebateAmount: line.rebate_amount || 0,
  parentAccount: line.parent_account_name,
  parentRewardsCode: line.parent_reward_number ?? null,
  farmName: line.child_account_name,
  farmRewardsCode: line.child_reward_number ?? null
});

/** Derive the visible-columns list from the saved column_config map. */
const resolveVisibleColumns = (columnConfig) =>
  Object.entries(columnConfig || {})
    .filter(([, visible]) => visible)
    .map(([key]) => ({ key }));

/**
 * Turn a saved statement into the full StatementPdf prop set.
 */
export const statementToPdfProps = (statement) => ({
  monthLabel: resolveMonthLabel(statement),
  visibleColumns: resolveVisibleColumns(statement.column_config),
  visibleInvoices: (statement.lines || []).map(toInvoice),
  grandTotal: statement.grand_total || 0,
  grandRebate: statement.grand_rebate || 0,
  reference: statement.reference || null
});

export default statementToPdfProps;
